import { ApiClient } from '~/api/Endpoints';
import { guestUser } from '~/api/user';
import { AUTHORIZED_FRONTEND_COOKIE_NAME } from '~/scripts/constants';
import { getCookie } from '~/scripts/cookie';
import isClient from '~/scripts/isClient';
import isServer from '~/scripts/isServer';
import { logger } from '~/scripts/logger';
import parseQueryFromSearchParams from '~/scripts/parseQueryFromSearchParams';
import type { Features, Suggestions } from '~/types';
import { Users } from '~/types/users';

export interface InitialData {
  user: Users.User;
  posthogIdentityHash: string | null;
  features: Features.Features;
  featuresAndVariables: Features.FeaturesAndVariables;
  industries: Suggestions.Industry[];
  categories: Suggestions.Category[];
  apiVersion: string;
  apiCommit: string;
}

export default async function getInitialData(
  request: Request | null
): Promise<InitialData> {
  const api = new ApiClient(request || undefined);

  const authorizedCookie = isServer()
    ? request?.headers.get('cookie')?.includes(AUTHORIZED_FRONTEND_COOKIE_NAME)
    : !!getCookie(AUTHORIZED_FRONTEND_COOKIE_NAME);

  let user: Users.User = guestUser;
  let posthogIdentityHash: string | null = null;
  if (authorizedCookie) {
    const userResponse = await api
      .get('/app/users/me', {
        headers: {
          cache: 'no-store',
        },
      })
      .catch(() => {
        return { user: guestUser, posthogIdentityHash: null };
      });
    user = userResponse?.user;
    posthogIdentityHash = userResponse?.posthogIdentityHash;
  }
  const url = new URL(request?.url || (isClient() ? window.location.href : ''));
  const featuresPromise: Promise<{
    features: Features.FeaturesAndVariables;
    apiVersion: string;
    apiCommit: string;
  }> = api
    .get('/app/features', {
      headers: {
        cache: 'no-store',
      },
    })
    .then(({ features, apiVersion, apiCommit }) => {
      const query = parseQueryFromSearchParams(url.searchParams);
      // find params that start with `features.`
      const overrides = Object.keys(query).filter((key) =>
        key.startsWith('features.')
      );
      overrides.forEach((key) => {
        const id = key.replace(/^features\./, '');
        const value = query[key];
        features[id] = features[id] || {
          variables: {},
          enabled: false,
        };
        if (value === 'true' || value === 'false') {
          features[id].enabled = value === 'true';
        }
      });
      return { features, apiVersion, apiCommit };
    })
    .catch((error) => {
      logger.error('Initial Data Failed', error);
      return {
        features: {} as Features.FeaturesAndVariables,
        apiVersion: '',
        apiCommit: '',
      };
    });
  const categoriesPromise = api
    .get('/suggestions/categories')
    .catch((error) => {
      logger.error('Categories Failed', error);
      return {
        categories: [] as Suggestions.Category[],
        industries: [] as Suggestions.Industry[],
      };
    });

  const [{ features, apiVersion, apiCommit }, { categories, industries }] =
    await Promise.all([featuresPromise, categoriesPromise]);

  const featuresMap = Object.keys(features).reduce(
    (acc, key) => {
      acc[key] = !!features[key]?.enabled;
      return acc;
    },
    {} as Record<string, boolean>
  );

  return {
    user,
    posthogIdentityHash,
    features: featuresMap,
    featuresAndVariables: features,
    categories,
    industries,
    apiVersion,
    apiCommit,
  };
}
